// The unfinished tastings for a screen: what this device holds, kept current as it changes, and
// what the edge adds (flights and live sessions the person is in; another device's copies when they
// turned that on). The edge half is read when a screen opens and when the app is back in view or
// online, never on a timer. A failed read leaves what was last known, so the list never blanks.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FlightSummary, SessionSummary, TastingDraftItem } from "@proef/contract";
import { listMyFlights } from "../edgeApi";
import { useSession } from "../social/auth";
import { listSessions } from "../social/sessionsApi";
import { listDrafts } from "./draftApi";
import { useDraftSyncEnabled } from "./draftSync";
import { useRuns } from "./store";
import { buildUnfinished, type UnfinishedItem } from "./unfinished";

interface Remote {
  readonly viewer: string | undefined;
  readonly drafts: readonly TastingDraftItem[];
  readonly flights: readonly FlightSummary[];
  readonly sessions: readonly SessionSummary[];
}

/** The last answer, shared by every screen that shows the list, so moving between them does not blink. */
let remembered: Remote | undefined;

async function read(viewer: string | undefined, drafts: boolean): Promise<Remote> {
  const [flights, sessions, kept] = await Promise.all([
    listMyFlights().then(
      (list) => list.items,
      () => undefined,
    ),
    viewer === undefined
      ? Promise.resolve([])
      : listSessions("live").then(
          (page) => page.items,
          () => undefined,
        ),
    drafts
      ? listDrafts().then(
          (list) => list.items,
          () => undefined,
        )
      : Promise.resolve([]),
  ]);
  const before = remembered?.viewer === viewer ? remembered : undefined;
  return {
    viewer,
    flights: flights ?? before?.flights ?? [],
    sessions: sessions ?? before?.sessions ?? [],
    drafts: kept ?? before?.drafts ?? [],
  };
}

export interface Unfinished {
  readonly items: readonly UnfinishedItem[];
  readonly refresh: () => void;
}

export function useUnfinished(): Unfinished {
  const runs = useRuns();
  const auth = useSession();
  const viewer = auth.status === "signedIn" ? auth.user.id : undefined;
  const keeping = useDraftSyncEnabled() === true;
  const [remote, setRemote] = useState<Remote | undefined>(() =>
    remembered?.viewer === viewer ? remembered : undefined,
  );

  const refresh = useCallback(() => {
    void read(viewer, keeping).then((fresh) => {
      remembered = fresh;
      setRemote(fresh);
      return undefined;
    });
  }, [viewer, keeping]);

  useEffect(() => {
    if (auth.status === "loading") return undefined;
    refresh();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", refresh);
    };
  }, [auth.status, refresh]);

  const known = remote?.viewer === viewer ? remote : undefined;
  const items = useMemo(
    () =>
      buildUnfinished({
        runs,
        drafts: keeping ? (known?.drafts ?? []) : [],
        flights: known?.flights ?? [],
        sessions: known?.sessions ?? [],
      }),
    [runs, keeping, known],
  );
  return { items, refresh };
}
